import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * Log geral do sistema (at-85, item 66) — tela `/ti/logs`, TI/ADMIN.
 *
 * 🔴 **Todas as funções LANÇAM.** O log é o conteúdo da tela inteira, e falhar em silêncio
 * mostraria "nenhum registro no período" — numa tela de auditoria, a conclusão mais cara
 * possível ("ninguém fez nada"). Mesma regra de `userApi` (at-68) e `oficinaApi` (at-53).
 */

export type TipoDeAtor = 'USUARIO' | 'PUBLICO' | 'INTEGRACAO' | 'SISTEMA';

export interface LogGeralItem {
  id: string;
  createdAt: string;
  tipo: 'REQUISICAO' | 'NEGOCIO';
  atorTipo: TipoDeAtor;
  quem: string;
  atorPapel: string | null;
  usuarioId: string | null;
  modulo: string | null;
  acao: string;
  resumo: string;
  entidade: string;
  entidadeId: string | null;
  registro: string;
  metodo: string | null;
  rota: string | null;
  statusCode: number | null;
  sucesso: boolean;
  requestId: string | null;
}

export interface LogGeralDetalhe extends LogGeralItem {
  ip: string | null;
  userAgent: string | null;
  payload: unknown;
  changes: Record<string, { antes: unknown; depois: unknown }> | null;
  linhasDaMesmaRequisicao: LogGeralItem[];
}

export interface PaginaLogGeral {
  itens: LogGeralItem[];
  pagina: number;
  limite: number;
  total: number;
  totalPaginas: number;
}

export interface OpcoesLogGeral {
  modulos: string[];
  acoes: string[];
  tiposDeAtor: { valor: TipoDeAtor; rotulo: string }[];
}

/** Os filtros aceitos pelo backend. ⚠️ Campo fora desta lista é recusado com 400. */
export interface FiltrosLogGeral {
  de?: string;
  ate?: string;
  pessoa?: string;
  atorTipo?: TipoDeAtor;
  modulo?: string;
  acao?: string;
  entidade?: string;
  entidadeId?: string;
  soFalhas?: boolean;
  q?: string;
  incluirNegocio?: boolean;
}

/** Só os filtros preenchidos vão na query (string vazia seria filtro por vazio). */
export function paraQuery(filtros: FiltrosLogGeral): Record<string, string> {
  const q: Record<string, string> = {};
  for (const [chave, valor] of Object.entries(filtros)) {
    if (valor === undefined || valor === null || valor === '' || valor === false) continue;
    q[chave] = String(valor);
  }
  return q;
}

export const listarLogGeral = async (
  filtros: FiltrosLogGeral,
  pagina: number,
  limite: number,
): Promise<PaginaLogGeral> => {
  try {
    const { data } = await http.get('/log-geral', {
      params: { ...paraQuery(filtros), pagina: String(pagina), limite: String(limite) },
    });
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível carregar o log do sistema.'));
  }
};

export const obterOpcoesLogGeral = async (): Promise<OpcoesLogGeral> => {
  try {
    const { data } = await http.get('/log-geral/opcoes');
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível carregar as opções de filtro.'));
  }
};

export const detalharLogGeral = async (id: string): Promise<LogGeralDetalhe> => {
  try {
    const { data } = await http.get(`/log-geral/${id}`);
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível abrir este registro.'));
  }
};

/**
 * Baixa o CSV (até 50.000 linhas). Devolve `cortado: true` quando o recorte passou do limite
 * — a tela avisa, senão o TI entregaria uma planilha incompleta achando que está inteira.
 */
export const exportarLogGeral = async (
  filtros: FiltrosLogGeral,
): Promise<{ blob: Blob; nomeDoArquivo: string; cortado: boolean }> => {
  try {
    const resposta = await http.get('/log-geral/export', {
      params: paraQuery(filtros),
      responseType: 'blob',
    });
    const disposicao = String(resposta.headers?.['content-disposition'] ?? '');
    const nome = /filename="([^"]+)"/.exec(disposicao)?.[1] ?? 'logs-saac.csv';
    return {
      blob: resposta.data,
      nomeDoArquivo: nome,
      cortado: String(resposta.headers?.['x-exportacao-cortada']) === 'true',
    };
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível exportar o log.'));
  }
};
